/**
 * AI predictions for the report's prediction column and the system analysis
 * panel, over the analysis AI services (createAiPredictionService,
 * createAiAnalysisService) and the Worker transport (POST /api/ai/predict,
 * /api/ai/analyze). The Worker's /api/health reports whether an OpenAI key
 * is configured: without one no request is sent at all, every row gets the
 * MTBF fallback (generateFallbackPrediction) and the panel gets the
 * algorithmic analysis built here, both flagged "Algorithm based".
 */
import {
  createAiAnalysisService,
  createAiPredictionService,
  createAiTransport,
  generateFallbackPrediction,
  getDefaultLifecycleKnowledge,
  type AiPrediction,
  type InventoryManager,
  type LifecycleKnowledge,
  type PredictionComponent,
  type SystemReportAnalysis,
} from '@/features/twin/analysis';
import type { Element } from '../../types';
import { parseDateMs } from '../lib/format';
import type { ReportRow } from './report';

export type { AiPrediction, LifecycleKnowledge };
export { getDefaultLifecycleKnowledge };

export type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'UNKNOWN';

export function riskLevelOf(level: string | undefined | null): RiskLevel {
  const l = (level ?? '').toUpperCase();
  return l === 'LOW' || l === 'MEDIUM' || l === 'HIGH' || l === 'CRITICAL' ? l : 'UNKNOWN';
}

export const LIFECYCLE_URL = '/data/knowledge/component-lifecycle.json';

interface HealthResponse {
  ok?: boolean;
  secrets?: { openai?: boolean };
}

/** Whether the Worker holds an OpenAI key (presence only, from /api/health). */
export async function fetchAiAvailability(): Promise<boolean> {
  const res = await fetch('/api/health', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`HTTP ${res.status} from /api/health`);
  const body = (await res.json()) as HealthResponse;
  return Boolean(body.secrets?.openai);
}

export interface AiDeps {
  /** False sends nothing to the model and uses the fallbacks. */
  configured: boolean;
  lifecycle: LifecycleKnowledge | null;
  inventory: InventoryManager | null;
  nowMs: () => number;
}

function transport() {
  return createAiTransport({ fetch: (input, init) => fetch(input, init) });
}

export function toPredictionComponent(row: ReportRow): PredictionComponent {
  return {
    id: row.id,
    name: row.name,
    category: row.category,
    status: row.level,
    statusColour: row.colour,
    health: row.health,
    healthTrend: row.healthTrend,
    data: row.source.data,
  };
}

export type BatchCallback = (prediction: AiPrediction, done: number, total: number) => void;

/** One prediction per row: the model through the service when configured, else the MTBF fallback with no request. */
export async function predictBatch(rows: ReportRow[], deps: AiDeps, onResult: BatchCallback): Promise<Record<string, AiPrediction>> {
  const components = rows.map(toPredictionComponent);
  if (!deps.configured) {
    const out: Record<string, AiPrediction> = {};
    components.forEach((c, i) => {
      const p = generateFallbackPrediction(c, deps.nowMs());
      out[c.id] = p;
      onResult(p, i + 1, components.length);
    });
    return out;
  }
  const service = createAiPredictionService({ transport: transport(), lifecycle: deps.lifecycle, inventory: deps.inventory, nowMs: deps.nowMs });
  return service.generateBatchPredictions(components, { includeInventory: true }, (_id, prediction, done, total) => onResult(prediction, done, total));
}

export interface SystemAnalysis {
  overallAssessment: string;
  riskLevel: RiskLevel;
  criticalIssues: string[];
  warningIssues: string[];
  recommendations: string[];
  maintenanceNeeds: { immediate: string[]; shortTerm: string[]; scheduled: string[] };
  inventoryActions: string[];
  predictedDowntime: string;
  confidenceLevel: number;
  isAIGenerated: boolean;
  generatedAtMs: number;
}

function fromServiceAnalysis(a: SystemReportAnalysis, nowMs: number): SystemAnalysis {
  const riskLevel = riskLevelOf(a.riskLevel);
  const confidence = typeof a.confidenceLevel === 'number' ? a.confidenceLevel : Number.parseFloat(String(a.confidenceLevel)) || 0;
  return {
    overallAssessment: a.overallAssessment,
    riskLevel,
    criticalIssues: a.criticalIssues ?? [],
    warningIssues: a.warningIssues ?? [],
    recommendations: a.recommendations ?? [],
    maintenanceNeeds: a.maintenanceNeeds ?? { immediate: [], shortTerm: [], scheduled: [] },
    inventoryActions: a.inventoryActions ?? [],
    predictedDowntime: a.predictedDowntime ?? 'Unknown',
    confidenceLevel: confidence,
    isAIGenerated: riskLevel !== 'UNKNOWN',
    generatedAtMs: parseDateMs(a._generatedAt ?? null) ?? nowMs,
  };
}

/** Whole-report analysis built from the rows and their predictions, no model involved. */
export function algorithmicAnalysis(rows: ReportRow[], predictions: Record<string, AiPrediction | undefined>, nowMs: number): SystemAnalysis {
  const days = (r: ReportRow) => predictions[r.id]?.daysUntilFailure ?? null;
  const critical = rows.filter((r) => r.colour === 'red');
  const warning = rows.filter((r) => r.colour === 'yellow');
  const soon = rows.filter((r) => days(r) !== null && (days(r) as number) <= 30);
  const within90 = rows.filter((r) => days(r) !== null && (days(r) as number) > 30 && (days(r) as number) <= 90);
  const within180 = rows.filter((r) => days(r) !== null && (days(r) as number) > 90 && (days(r) as number) <= 180);
  const lowStock = rows.filter((r) => r.inventory && r.inventory.quantity <= (r.inventory.minStock || 1));
  const outOfStock = rows.filter((r) => r.inventory && r.inventory.quantity === 0);

  const riskLevel: RiskLevel = critical.length > 0 || soon.length > 0 ? 'CRITICAL' : within90.length > 0 ? 'HIGH' : warning.length > 0 || within180.length > 0 ? 'MEDIUM' : 'LOW';
  const overallAssessment =
    riskLevel === 'LOW'
      ? `${rows.length} components tracked, all operational; no failure projected within 180 days.`
      : `${rows.length} components tracked: ${critical.length} critical, ${warning.length} in warning, ${soon.length} projected to fail within 30 days and ${within90.length} within 90 days.`;

  const recommendations: string[] = [];
  for (const r of soon) recommendations.push(`${r.name}: ${predictions[r.id]?.recommendedAction ?? 'replace now'}`);
  for (const r of critical.filter((c) => !soon.includes(c))) recommendations.push(`${r.name}: investigate the critical readings and plan a replacement.`);
  for (const r of within90) recommendations.push(`${r.name}: ${predictions[r.id]?.recommendedAction ?? 'schedule maintenance'}`);
  if (recommendations.length === 0) recommendations.push('Continue the standard monitoring and maintenance schedule.');

  const inventoryActions: string[] = [];
  for (const r of outOfStock) inventoryActions.push(`Order ${r.inventory!.name} (${r.inventory!.partNumber}): out of stock, ${r.inventory!.leadTimeDays} day lead time.`);
  for (const r of lowStock.filter((l) => !outOfStock.includes(l))) inventoryActions.push(`Restock ${r.inventory!.name} (${r.inventory!.partNumber}): ${r.inventory!.quantity} left, minimum ${r.inventory!.minStock}.`);

  const names = (list: ReportRow[]) => list.map((r) => r.name);
  const made = Object.values(predictions).filter((p): p is AiPrediction => Boolean(p));
  const confidenceLevel = made.length === 0 ? 0 : Math.round(made.reduce((sum, p) => sum + (p.confidence ?? 0), 0) / made.length);
  return {
    overallAssessment,
    riskLevel,
    criticalIssues: [...names(critical).map((n) => `${n} is reporting critical readings.`), ...names(soon).map((n) => `${n} is projected to fail within 30 days.`)],
    warningIssues: [...names(warning).map((n) => `${n} is in warning.`), ...names(within90).map((n) => `${n} is projected to fail within 90 days.`)],
    recommendations,
    maintenanceNeeds: { immediate: names(soon), shortTerm: names([...critical.filter((c) => !soon.includes(c)), ...within90]), scheduled: names(within180) },
    inventoryActions,
    predictedDowntime: soon.length > 0 ? 'High: a failure within 30 days would interrupt service' : within90.length > 0 ? 'Moderate: plan the replacements in the next maintenance window' : 'Low',
    confidenceLevel,
    isAIGenerated: false,
    generatedAtMs: nowMs,
  };
}

/**
 * System analysis: the model through the service when configured, else the
 * algorithmic analysis of the rows. `name` is the asset's title
 * (data/assetName.ts); the service names the system in its prompt by the
 * element's callsign, so the element goes in with the title as its callsign
 * (a parametric antenna twin shares the rf_antenna element).
 */
export async function analyzeSystemReport(element: Element, rows: ReportRow[], predictions: Record<string, AiPrediction | undefined>, deps: AiDeps, name?: string): Promise<SystemAnalysis> {
  if (!deps.configured) return algorithmicAnalysis(rows, predictions, deps.nowMs());
  const service = createAiAnalysisService({ transport: transport(), nowMs: deps.nowMs, inventory: deps.inventory });
  const named = name !== undefined && name !== element.callsign ? { ...element, callsign: name } : element;
  return fromServiceAnalysis(await service.analyzeSystemReport(named), deps.nowMs());
}
